import MobileMenu, { MobileMenuCSS } from "./MobileMenu";
import { supabase } from "./supabaseClient";
import { useState, useEffect, useRef, useCallback } from "react";

/* ─────────────────────────────────────────────────────────
   CENSUS API CONFIG
   Source: U.S. Census Bureau, ACS 5-Year Estimates (2022)
   Geography: Michigan State Legislative Districts
   Set REACT_APP_CENSUS_API_KEY in your .env file
───────────────────────────────────────────────────────── */
const CENSUS_KEY = process.env.REACT_APP_CENSUS_API_KEY || "";
const CENSUS_YEAR = "2022";
const CENSUS_BASE = `https://api.census.gov/data/${CENSUS_YEAR}/acs/acs5`;

// ACS 5-Year variable codes
const ACS_VARS = [
  "B01003_001E",  // Total population
  "B19013_001E",  // Median household income (past 12 months, inflation-adjusted)
  "B15003_001E",  // Population 25+ (education denominator)
  "B15003_022E",  // Bachelor's degree
  "B15003_023E",  // Master's degree
  "B15003_024E",  // Professional school degree
  "B15003_025E",  // Doctorate degree
  "B23025_002E",  // Civilian labor force
  "B23025_005E",  // Unemployed (civilian labor force)
  "C24050_001E",  // Total civilian employed 16+ (denominator)
  "C24050_002E",  // Agriculture, forestry, fishing/hunting, and mining
  "C24050_003E",  // Construction
  "C24050_004E",  // Manufacturing
  "C24050_005E",  // Wholesale trade
  "C24050_006E",  // Retail trade
  "C24050_007E",  // Transportation and warehousing, and utilities
  "C24050_008E",  // Information
  "C24050_009E",  // Finance, insurance, real estate, rental, leasing
  "C24050_010E",  // Professional, scientific, management, admin, waste mgmt
  "C24050_011E",  // Educational services, health care, social assistance
  "C24050_012E",  // Arts, entertainment, accommodation, food services
  "C24050_013E",  // Other services (except public administration)
  "C24050_014E",  // Public administration
].join(",");

const INDUSTRY_LABELS = {
  C24050_002E: "Agriculture & Mining",
  C24050_003E: "Construction",
  C24050_004E: "Manufacturing",
  C24050_005E: "Wholesale Trade",
  C24050_006E: "Retail Trade",
  C24050_007E: "Transportation & Utilities",
  C24050_008E: "Information",
  C24050_009E: "Finance & Real Estate",
  C24050_010E: "Professional & Management",
  C24050_011E: "Healthcare & Education",
  C24050_012E: "Hospitality & Food",
  C24050_013E: "Other Services",
  C24050_014E: "Public Administration",
};

// Cache fetched data to avoid re-fetching on every click
const censusCache = { Senate: null, House: null };

async function fetchCensusDistricts(chamber) {
  if (censusCache[chamber]) return censusCache[chamber];

  const geoType = chamber === "Senate"
    ? "state+legislative+district+(upper+chamber)"
    : "state+legislative+district+(lower+chamber)";

  const url = `${CENSUS_BASE}?get=NAME,${ACS_VARS}&for=${geoType}:*&in=state:26&key=${CENSUS_KEY}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Census API returned ${res.status}. Check your API key in .env.`);

  const raw = await res.json();
  const headers = raw[0];
  const rows = raw.slice(1);

  const byDistrict = {};
  rows.forEach(row => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = row[i]; });

    // District number is in the last column
    const distKey = headers[headers.length - 1];
    const distNum = parseInt(obj[distKey], 10);
    if (isNaN(distNum)) return;

    const pop        = parseInt(obj.B01003_001E) || 0;
    const rawIncome  = parseInt(obj.B19013_001E);
    const income     = (rawIncome && rawIncome > 0) ? rawIncome : null; // -666666666 = not available
    const edu_total  = parseInt(obj.B15003_001E) || 1;
    const edu_higher = (parseInt(obj.B15003_022E) || 0) + (parseInt(obj.B15003_023E) || 0)
                     + (parseInt(obj.B15003_024E) || 0) + (parseInt(obj.B15003_025E) || 0);
    const laborForce = parseInt(obj.B23025_002E) || 1;
    const unemployed = parseInt(obj.B23025_005E) || 0;
    const indTotal   = parseInt(obj.C24050_001E) || 1;

    const industries = Object.entries(INDUSTRY_LABELS)
      .map(([code, label]) => ({
        label,
        count: parseInt(obj[code]) || 0,
        pct: parseFloat(((parseInt(obj[code]) || 0) / indTotal * 100).toFixed(1)),
      }))
      .filter(i => i.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    byDistrict[distNum] = {
      population: pop,
      medianIncome: income,
      pctCollege: parseFloat((edu_higher / edu_total * 100).toFixed(1)),
      pctUnemployment: parseFloat((unemployed / laborForce * 100).toFixed(1)),
      laborForce,
      industries,
      censusName: obj.NAME,
    };
  });

  censusCache[chamber] = byDistrict;
  return byDistrict;
}

/* ─── LEGISLATOR DATA ───────────────────────────────────── */
const SENATORS = [
  { id:"s1",  district:1,  name:"Erika Geiss",        party:"D", region:"Taylor / Downriver",         ideology:3, keyIssues:["Education funding","Civil rights","Labor protections"],         committees:["Education (Chair)","Economic & Community Dev.","Health Policy","Housing & Human Services","Oversight"], bio:"Progressive voice from Wayne County. Strong labor and education advocate with deep community ties in Downriver communities." },
  { id:"s2",  district:2,  name:"Sylvia Santana",     party:"D", region:"Detroit",                    ideology:2, keyIssues:["Healthcare access","Detroit revitalization","Housing"],         committees:["Appropriations","Civil Rights, Judiciary & Public Safety","Elections & Ethics","Health Policy (Vice Chair)","Housing & Human Services (Vice Chair)"], bio:"Detroit-based senator focused on healthcare equity and urban investment. Reliable Democratic vote on social policy." },
  { id:"s3",  district:3,  name:"Stephanie Chang",    party:"D", region:"Detroit",                    ideology:2, keyIssues:["Environmental justice","Criminal justice reform","Clean energy"], committees:["Civil Rights, Judiciary & Public Safety (Chair)","Education","Elections & Ethics","Energy & Environment","Housing & Human Services"], bio:"Environmental justice champion. Former House member with broad policy expertise, especially on climate and criminal justice." },
  { id:"s4",  district:4,  name:"Darrin Camilleri",   party:"D", region:"Brownstown / Rockwood",      ideology:4, keyIssues:["Labor rights","Education","Working families"],                  committees:["Appropriations","Energy & Environment","Finance, Insurance & Consumer Protection","Labor (Vice Chair)","PreK-12 Appropriations (Chair)"], bio:"Labor-focused senator from a competitive suburban district. Former teacher and staunch union advocate." },
  { id:"s5",  district:5,  name:"Dayna Polehanki",    party:"D", region:"Livonia / Westland",         ideology:5, keyIssues:["Education","Local governance","Women's rights"],               committees:["Education (Chair)","Economic & Community Dev.","Energy & Environment","Natural Resources & Agriculture","Oversight","Regulatory Affairs (Vice Chair)"], bio:"Moderate Democrat from suburban Wayne County. Education-focused with strong constituent services record." },
  { id:"s6",  district:6,  name:"Mary Cavanagh",      party:"D", region:"Redford / Dearborn Heights", ideology:4, keyIssues:["Public safety","Consumer protection","Budget"],                committees:["Finance, Insurance & Consumer Protection (Chair)","Appropriations","Economic & Community Dev. (Vice Chair)","Housing & Human Services","Labor"], bio:"Pragmatic Democrat from a heavily populated district. Focus on fiscal responsibility and public safety." },
  { id:"s7",  district:7,  name:"Jeremy Moss",        party:"D", region:"Southfield",                 ideology:3, keyIssues:["LGBTQ+ rights","Tax policy","Financial regulation"],            committees:["Elections & Ethics (Chair)","Regulatory Affairs (Chair)","Economic & Community Dev.","Government Operations","Local Government"], bio:"President Pro Tem. Progressive Southfield senator and leading LGBTQ+ rights voice in the legislature." },
  { id:"s8",  district:8,  name:"Mallory McMorrow",   party:"D", region:"Royal Oak / Troy",           ideology:4, keyIssues:["Infrastructure","Transit","Tech economy","LGBTQ+ rights"],     committees:["Economic & Community Dev. (Chair)","Appropriations","Elections & Ethics","JCAR","Oversight (Vice Chair)"], bio:"High-profile Democrat known for viral floor speech on LGBTQ+ rights. Suburban Oakland County moderate with national profile." },
  { id:"s9",  district:9,  name:"Michael Webber",     party:"R", region:"Rochester Hills",            ideology:7, keyIssues:["Small business","Lower taxes","Healthcare cost"],              committees:["Economic & Community Dev.","Health Policy (Vice Chair)"], bio:"Rochester Hills Republican focused on small business and economic development. Moderate within GOP caucus." },
  { id:"s10", district:10, name:"Paul Wojno",         party:"D", region:"Warren",                     ideology:4, keyIssues:["Labor rights","Manufacturing","Regulatory balance"],           committees:["Elections & Ethics (Vice Chair)","Civil Rights, Judiciary & Public Safety","Health Policy","JCAR","Local Government (Vice Chair)","Regulatory Affairs"], bio:"Warren-based senator with strong labor ties. Represents a heavily union Macomb County district." },
  { id:"s11", district:11, name:"Veronica Klinefelt", party:"D", region:"Eastpointe / St. Clair Shores", ideology:5, keyIssues:["Infrastructure","Local government funding","Fiscal policy"], committees:["Local Government (Chair)","Veterans & Emergency Services (Chair)","Appropriations","Health Policy","Transportation Appropriations (Chair)"], bio:"Pragmatic Democrat focused on funding local governments and infrastructure in the Macomb/Wayne area." },
  { id:"s12", district:12, name:"Kevin Hertel",       party:"D", region:"St. Clair Shores / Clinton Twp", ideology:5, keyIssues:["Transportation","Criminal justice","Constituent services"], committees:["Health Policy (Chair)","Appropriations","Energy & Environment","Veterans & Emergency Services (Vice Chair)"], bio:"Macomb County Democrat with strong constituent services record. Works across the aisle on transportation issues." },
  { id:"s13", district:13, name:"Rosemary Bayer",     party:"D", region:"Beverly Hills / Clawson",    ideology:4, keyIssues:["Environment","Water quality","Fiscal accountability"],        committees:["Energy & Environment","Finance, Insurance & Consumer Protection","Housing & Human Services","JCAR (Vice Chair)","Local Government","Natural Resources & Agriculture"], bio:"Oakland County Democrat with focus on environmental protection and PFAS cleanup. Data-driven legislator." },
  { id:"s14", district:14, name:"Sue Shink",          party:"D", region:"Ann Arbor area",             ideology:3, keyIssues:["Environmental law","Clean water","Criminal justice"],          committees:["Natural Resources & Agriculture (Chair)","Civil Rights, Judiciary & Public Safety (Vice Chair)","Appropriations - Corrections & Judiciary (Chair)"], bio:"Environmental attorney turned senator. Strong progressive on climate and judicial reform." },
  { id:"s15", district:15, name:"Jeff Irwin",         party:"D", region:"Ann Arbor",                  ideology:2, keyIssues:["Higher education","Tax policy","Civil liberties"],             committees:["Housing & Human Services (Chair)","Civil Rights, Judiciary & Public Safety","Education","Finance, Insurance & Consumer Protection (Vice Chair)"], bio:"Ann Arbor progressive with deep policy expertise. Former longtime House member who brings legislative experience to the Senate." },
  { id:"s16", district:16, name:"Joseph Bellino",     party:"R", region:"Monroe",                     ideology:7, keyIssues:["Transportation","Economic development","Law enforcement"],    committees:["Energy & Environment","Regulatory Affairs","Transportation & Infrastructure (Vice Chair)","Veterans & Emergency Services"], bio:"Monroe County Republican. Former mayor with focus on transportation infrastructure and law enforcement support." },
  { id:"s17", district:17, name:"Jonathan Lindsey",   party:"R", region:"Jackson area",               ideology:8, keyIssues:["Agriculture","Rural issues","Property rights"],               committees:["Housing & Human Services (Minority Vice Chair)","Economic & Community Dev.","Natural Resources & Agriculture","Oversight"], bio:"Rural conservative focused on agricultural policy and property rights. Consistent Republican vote on most issues." },
  { id:"s18", district:18, name:"Thomas Albert",      party:"R", region:"Lowell / Ionia area",        ideology:8, keyIssues:["Fiscal responsibility","Budget","Tax cuts"],                  committees:["Appropriations","Labor (Minority Vice Chair)"], bio:"Former House Appropriations chair now in Senate. Strong fiscal conservative with deep budget expertise." },
  { id:"s19", district:19, name:"Sean McCann",        party:"D", region:"Kalamazoo",                  ideology:4, keyIssues:["Healthcare","Appropriations","Housing"],                      committees:["Energy & Environment (Chair)","Appropriations (Vice Chair)","Finance, Insurance & Consumer Protection"], bio:"Kalamazoo Democrat with healthcare focus. Serves on Appropriations and understands budget mechanics well." },
  { id:"s20", district:20, name:"Aric Nesbitt",       party:"R", region:"Porter Township",            ideology:9, keyIssues:["Deregulation","Tax cuts","Business climate"],                committees:["Government Operations (Minority Vice Chair)"], bio:"Minority Leader. Strong conservative from Southwest Michigan. Consistent opposition to Democratic agenda; key vote-counter for GOP." },
  { id:"s21", district:21, name:"Sarah Anthony",      party:"D", region:"Lansing",                    ideology:3, keyIssues:["State budget","Community investment","Equity"],               committees:["Appropriations (Chair)","Government Operations","General Government Appropriations (Chair)"], bio:"Lansing Democrat with appropriations expertise. Focuses on equitable budget allocations and community investment." },
  { id:"s22", district:22, name:"Lana Theis",         party:"R", region:"Brighton",                   ideology:8, keyIssues:["School choice","Parental rights","Education reform"],         committees:["Appropriations","Finance, Insurance & Consumer Protection","JCAR (Minority Vice Chair)"], bio:"Livingston County conservative. Education traditionalist focused on school choice and parental rights. Active on social issues." },
  { id:"s23", district:23, name:"Jim Runestad",       party:"R", region:"White Lake / Commerce Twp",  ideology:8, keyIssues:["Criminal justice","Deregulation","Property rights"],         committees:["Civil Rights, Judiciary & Public Safety (Minority Vice Chair)","Health Policy","JCAR"], bio:"Oakland County Republican. Strong on criminal justice from a law enforcement perspective. Skeptical of regulatory expansion." },
  { id:"s24", district:24, name:"Ruth Johnson",       party:"R", region:"Holly / Clarkston area",     ideology:7, keyIssues:["Election integrity","Local governance","Taxes"],              committees:["Civil Rights, Judiciary & Public Safety","Education (Minority Vice Chair)","Elections & Ethics (Minority Vice Chair)"], bio:"Former Secretary of State. Election integrity focus. Moderate Republican with statewide credentials." },
  { id:"s25", district:25, name:"Dan Lauwers",        party:"R", region:"Thumb / Sanilac area",       ideology:8, keyIssues:["Agriculture","Natural resources","Rural economy"],            committees:["Government Operations","Energy & Environment (Minority Vice Chair)","Natural Resources & Agriculture"], bio:"Thumb region conservative. Deep agricultural roots; reliable Republican vote on farm and rural issues." },
  { id:"s26", district:26, name:"Kevin Daley",        party:"R", region:"Lapeer / Tuscola",           ideology:8, keyIssues:["Agriculture","Rural roads","Property rights"],               committees:["Finance, Insurance & Consumer Protection","Local Government","Natural Resources & Agriculture (Minority Vice Chair)"], bio:"Rural Republican from the Thumb. Agriculture and transportation focus; votes with GOP caucus consistently." },
  { id:"s27", district:27, name:"John Cherry",        party:"D", region:"Flint / Genesee",            ideology:4, keyIssues:["Flint recovery","Economic development","Water infrastructure"], committees:["Labor (Chair)","Appropriations","Agriculture & Natural Resources Appropriations (Chair)"], bio:"Genesee County Democrat. Focused on Flint's continued recovery and regional economic development." },
  { id:"s28", district:28, name:"Sam Singh",          party:"D", region:"East Lansing / Meridian",    ideology:4, keyIssues:["University funding","Tech economy","Tax policy"],             committees:["Oversight (Chair)","Energy & Environment (Vice Chair)","Government Operations (Vice Chair)"], bio:"East Lansing Democrat with strong higher education and technology focus. Moderate on fiscal issues." },
  { id:"s29", district:29, name:"Winnie Brinks",      party:"D", region:"Grand Rapids",               ideology:4, keyIssues:["Budget","Healthcare","Urban investment"],                     committees:["Government Operations (Chair)","Appropriations"], bio:"Senate Majority Leader. Grand Rapids Democrat navigating a competitive urban district. Pragmatic coalition-builder." },
  { id:"s30", district:30, name:"Mark Huizenga",      party:"R", region:"Walker / Wyoming area",      ideology:7, keyIssues:["Fiscal policy","Insurance reform","Business"],                committees:["Appropriations","Finance, Insurance & Consumer Protection (Minority Vice Chair)","Health Policy"], bio:"West Michigan Republican. Finance focus and business-friendly. Moderate within GOP — sometimes a swing vote on fiscal matters." },
  { id:"s31", district:31, name:"Roger Victory",      party:"R", region:"West Michigan / Ottawa",     ideology:8, keyIssues:["Agriculture","Water rights","Rural economy"],                committees:["Economic & Community Dev. (Minority Vice Chair)","Natural Resources & Agriculture","Transportation & Infrastructure"], bio:"Ottawa County Republican. Strong agricultural policy focus. Consistently conservative on most votes." },
  { id:"s32", district:32, name:"Jon Bumstead",       party:"R", region:"Newaygo / Muskegon area",    ideology:7, keyIssues:["Natural resources","Fishing","Rural infrastructure"],        committees:["Appropriations (Minority Vice Chair)","Transportation & Infrastructure"], bio:"West Michigan Republican with strong natural resource and fishing industry ties." },
  { id:"s33", district:33, name:"Rick Outman",        party:"R", region:"Six Lakes / Montcalm",       ideology:9, keyIssues:["Deregulation","Agriculture","Small government"],             committees:["Appropriations","Energy & Environment","Transportation & Infrastructure"], bio:"Very conservative rural Republican. Strong deregulation advocate and consistent GOP caucus vote." },
  { id:"s34", district:34, name:"Roger Hauck",        party:"R", region:"Isabella / Clare area",      ideology:8, keyIssues:["Energy policy","Natural resources","Rural economy"],         committees:["Regulatory Affairs (Minority Vice Chair)","Energy & Environment","Health Policy"], bio:"Mid-Michigan Republican with energy and natural resources focus. Reliable conservative vote." },
  { id:"s35", district:35, name:"[Vacant]",           party:"-", region:"Mid-Michigan",               ideology:5, keyIssues:[],                                                            committees:[], bio:"Seat currently vacant." },
  { id:"s36", district:36, name:"Michele Hoitenga",   party:"R", region:"Manton / Cadillac area",     ideology:8, keyIssues:["Healthcare freedom","Parental rights","Rural health"],      committees:["Housing & Human Services","Local Government (Minority Vice Chair)","Natural Resources & Agriculture"], bio:"Northern Michigan conservative. Focused on healthcare freedom and local governance. Strong GOP vote." },
  { id:"s37", district:37, name:"John Damoose",       party:"R", region:"Harbor Springs / Petoskey",  ideology:7, keyIssues:["Tourism","Natural resources","Northern MI economy"],         committees:["Appropriations","Economic & Community Dev.","Education (Minority Vice Chair)"], bio:"Northern Michigan Republican focused on tourism and the region's economy. Moderate on some economic issues." },
  { id:"s38", district:38, name:"Ed McBroom",         party:"R", region:"Upper Peninsula",            ideology:8, keyIssues:["Mining","UP economy","Natural resources"],                   committees:["Elections & Ethics","Oversight (Minority Vice Chair)","Transportation & Infrastructure"], bio:"UP Republican deeply focused on mining, forestry, and rural UP issues. Reliable conservative on most votes." },
];

const REPS_RAW = [
  [1,"Tyrone Carter","D","Wayne"],[2,"Tullio Liberati","D","Wayne"],[3,"Alabas Farhat","D","Wayne"],[4,"Karen Whitsett","D","Wayne"],[5,"Regina Weiss","D","Oakland/Wayne"],[6,"Natalie Price","D","Oakland/Wayne"],[7,"Tonya Myers Phillips","D","Oakland/Wayne"],[8,"Helena Scott","D","Oakland/Wayne"],[9,"Joe Tate","D","Wayne"],[10,"Veronica Paiz","D","Macomb/Wayne"],[11,"Donavan McKinney","D","Macomb/Wayne"],[12,"Kimberly Edwards","D","Macomb/Wayne"],[13,"Mai Xiong","D","Macomb/Wayne"],[14,"Mike McFall","D","Macomb/Wayne"],[15,"Erin Byrnes","D","Wayne"],[16,"Stephanie Young","D","Wayne"],[17,"Laurie Pohutsky","D","Wayne"],[18,"Jason Hoskins","D","Oakland"],[19,"Samantha Steckloff","D","Oakland"],[20,"Noah Arbit","D","Oakland"],[21,"Kelly Breen","D","Oakland"],[22,"Matt Koleszar","D","Wayne"],[23,"Jason Morgan","D","Oakland/Washtenaw/Wayne"],[24,"Ranjeev Puri","D","Wayne"],[25,"Peter Herzberg","D","Wayne"],[26,"Dylan Wegela","D","Wayne"],[27,"Rylee Linting","R","Wayne"],[28,"Jamie Thompson","R","Monroe/Wayne"],[29,"James DeSana","R","Monroe/Wayne"],[30,"William Bruck","R","Lenawee/Monroe"],[31,"Reggie Miller","D","Lenawee/Monroe/Washtenaw/Wayne"],[32,"Jimmie Wilson Jr.","D","Washtenaw"],[33,"Morgan Foreman","D","Washtenaw"],[34,"Nancy Jenkins-Arno","R","Lenawee"],[35,"Jennifer Wortz","R","Branch/Hillsdale/Lenawee"],[36,"Steve Carra","R","Cass/St. Joseph"],[37,"Brad Paquette","R","Berrien/Cass"],[38,"Joey Andrews","D","Allegan/Berrien/Van Buren"],[39,"Pauline Wendzel","R","Allegan/Berrien/Van Buren"],[40,"Matthew Longjohn","D","Kalamazoo"],[41,"Julie Rogers","D","Kalamazoo"],[42,"Matt Hall","R","Allegan/Kalamazoo"],[43,"Rachelle Smit","R","Allegan/Barry/Eaton/Ottawa"],[44,"Steve Frisbie","R","Calhoun"],[45,"Sarah Lightner","R","Calhoun/Kalamazoo/Jackson"],[46,"Kathy Schmaltz","R","Jackson/Washtenaw"],[47,"Carrie Rheingans","D","Jackson/Washtenaw"],[48,"Jennifer Conlin","D","Jackson/Livingston/Washtenaw"],[49,"Ann Bollin","R","Livingston/Oakland"],[50,"Jason Woolford","R","Livingston"],[51,"Matt Maddock","R","Oakland"],[52,"Mike Harris","R","Oakland"],[53,"Brenda Carter","D","Oakland"],[54,"Donni Steele","R","Oakland"],[55,"Mark Tisdel","R","Oakland"],[56,"Sharon MacDonell","D","Oakland"],[57,"Thomas Kuhn","R","Macomb/Oakland"],[58,"Ron Robinson","R","Macomb"],[59,"Doug Wozniak","R","Macomb"],[60,"Joseph Aragona","R","Macomb"],[61,"Denise Mentzer","D","Macomb"],[62,"Alicia St. Germaine","R","Macomb"],[63,"Jay DeBoyer","R","Macomb/St. Clair"],[64,"Joseph Pavlov","R","Sanilac/St. Clair"],[65,"Jaime Greene","R","Lapeer/Macomb/St. Clair"],[66,"Josh Schriver","R","Macomb/Oakland"],[67,"Phil Green","R","Genesee/Lapeer/Tuscola"],[68,"David Martin","R","Genesee/Oakland"],[69,"Jasper Martus","D","Genesee"],[70,"Cynthia Neeley","D","Genesee"],[71,"Brian BeGole","R","Genesee/Saginaw/Shiawassee"],[72,"Mike Mueller","R","Genesee/Livingston/Oakland"],[73,"Julie Brixie","D","Ingham"],[74,"Kara Hope","D","Ingham"],[75,"Penelope Tsernoglou","D","Clinton/Ingham/Shiawassee"],[76,"Angela Witwer","D","Eaton"],[77,"Emily Dievendorf","D","Clinton/Eaton/Ingham"],[78,"Gina Johnsen","R","Barry/Eaton/Ionia/Kent"],[79,"Angela Rigas","R","Allegan/Barry/Kent"],[80,"Phil Skaggs","D","Kent"],[81,"Stephen Wooden","D","Kent"],[82,"Kristian Grant","D","Kent"],[83,"John Fitzgerald","D","Kent"],[84,"Carol Glanville","D","Kent"],[85,"Bradley Slagh","R","Ottawa"],[86,"Nancy De Boer","R","Allegan/Ottawa"],[87,"Will Snyder","D","Muskegon"],[88,"Greg VanWoerkom","R","Muskegon/Ottawa"],[89,"Luke Meerman","R","Kent/Muskegon/Ottawa"],[90,"Bryan Posthumus","R","Kent"],[91,"Pat Outman","R","Ionia/Kent/Montcalm"],[92,"Jerry Neyer","R","Gratiot/Isabella"],[93,"Tim Kelly","R","Clinton/Gratiot/Ionia/Montcalm/Saginaw"],[94,"Amos O'Neal","D","Saginaw"],[95,"Bill G. Schuette","R","Gladwin/Midland"],[96,"Timothy Beson","R","Bay"],[97,"Matthew Bierlein","R","Bay/Genesee/Saginaw/Tuscola"],[98,"Gregory Alexander","R","Huron/Lapeer/Sanilac/Tuscola"],[99,"Mike Hoadley","R","Arenac/Bay/Clare/Gladwin/Iosco/Ogemaw"],[100,"Tom Kunse","R","Clare/Lake/Mecosta/Osceola"],[101,"Joseph Fox","R","Lake/Mason/Newaygo/Oceana/Wexford"],[102,"Curt VanderWall","R","Manistee/Mason/Muskegon/Oceana"],[103,"Betsy Coffia","D","Benzie/Grand Traverse/Leelanau"],[104,"John Roth","R","Antrim/Benzie/Grand Traverse/Kalkaska/Manistee/Wexford"],[105,"Ken Borton","R","Antrim/Crawford/Kalkaska/Missaukee/Oscoda/Otsego/Roscommon"],[106,"Cam Cavitt","R","Alcona/Alpena/Cheboygan/Montmorency/Oscoda/Presque Isle"],[107,"Parker Fairbairn","R","Charlevoix/Chippewa/Emmet/Mackinac"],[108,"David Prestin","R","Chippewa/Delta/Luce/Mackinac/Menominee/Schoolcraft"],[109,"Karl Bohnak","R","Alger/Baraga/Dickinson/Marquette"],[110,"Gregory Markkanen","R","Dickinson/Gogebic/Houghton/Iron/Keweenaw/Ontonagon"],
];

const REPRESENTATIVES = REPS_RAW.map(([district, name, party, region]) => ({
  id: `h${district}`, district, name, party, region, chamber: "House",
  keyIssues: [], committees: [],
  bio: `${name} represents House District ${district}, covering parts of ${region}.`,
}));

/* ─── STYLES ────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
/* Theme variables defined globally in public/theme.css */
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}
.map-app{display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--bg);}
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.legisim-masthead::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(201,168,76,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,0.025) 1px,transparent 1px);background-size:44px 44px;pointer-events:none;}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.5);color:var(--red);}
.masthead-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:var(--muted);vertical-align:super;}
.masthead-divider{width:1px;height:20px;background:var(--border);}
.masthead-tagline{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;}
.masthead-badge{display:inline-flex;align-items:center;gap:0.3rem;background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.2);border-radius:100px;padding:0.18rem 0.6rem;font-family:var(--font-mono);font-size:0.54rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;}
.masthead-badge-dot{width:5px;height:5px;border-radius:50%;background:var(--gold);animation:mbpulse 2.5s infinite;}
@keyframes mbpulse{0%,100%{opacity:1}50%{opacity:0.4}}
.masthead-nav{display:flex;align-items:center;gap:0.3rem;z-index:1;flex-shrink:1;overflow:hidden;}
.mast-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.36rem 0.7rem;color:var(--muted);font-family:var(--font-mono);font-size:0.64rem;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-btn:hover{border-color:var(--gold-dim);color:var(--gold);background:rgba(201,168,76,0.05);}
.mast-btn.gold{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn.gold:hover{background:rgba(201,168,76,0.1);}
.mast-btn.active{border-color:var(--gold);color:var(--gold);background:rgba(201,168,76,0.1);}
.mast-sep{width:1px;height:18px;background:var(--border);margin:0 0.2rem;}
.map-body{display:flex;flex:1;overflow:hidden;}
.map-left{width:300px;flex-shrink:0;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;}
.map-left-header{padding:1rem 1.25rem;border-bottom:1px solid var(--border);flex-shrink:0;}
.map-left-title{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;margin-bottom:0.75rem;}
.search-wrap{position:relative;}
.search-icon{position:absolute;left:0.65rem;top:50%;transform:translateY(-50%);width:14px;height:14px;stroke:var(--muted);fill:none;stroke-width:2;stroke-linecap:round;pointer-events:none;}
.search-input{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:6px;padding:0.55rem 0.85rem 0.55rem 2rem;color:var(--text);font-family:var(--font-body);font-size:0.83rem;outline:none;transition:border-color 0.2s;}
.search-input:focus{border-color:var(--gold-dim);}
.search-input::placeholder{color:var(--muted);}
.search-results{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.search-section-label{padding:0.4rem 1.25rem;font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;border-bottom:1px solid var(--border);background:var(--surface);position:sticky;top:0;z-index:2;}
.search-result-item{display:flex;align-items:center;gap:0.75rem;padding:0.7rem 1.25rem;cursor:pointer;border-bottom:1px solid var(--border);transition:background 0.15s;}
.search-result-item:hover{background:var(--surface2);}
.search-result-item.selected{background:var(--charcoal);}
.sri-avatar{width:32px;height:32px;border-radius:6px;display:flex;align-items:center;justify-content:center;font-family:var(--font-display);font-weight:800;font-size:0.78rem;flex-shrink:0;}
.sri-avatar.D{background:var(--dem-bg);color:var(--dem);}
.sri-avatar.R{background:var(--rep-bg);color:var(--rep);}
.sri-name{font-size:0.8rem;font-weight:500;color:var(--text);line-height:1.2;}
.sri-sub{font-size:0.67rem;color:var(--muted);font-family:var(--font-mono);}
.sri-badge{margin-left:auto;font-size:0.58rem;font-family:var(--font-mono);padding:0.15rem 0.4rem;border-radius:4px;flex-shrink:0;}
.sri-badge.D{background:var(--dem-bg);color:var(--dem);}
.sri-badge.R{background:var(--rep-bg);color:var(--rep);}
.map-center{flex:1;display:flex;flex-direction:column;overflow:hidden;position:relative;background:var(--bg);}
.map-toolbar{padding:0.75rem 1.25rem;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:1rem;background:var(--surface);flex-shrink:0;flex-wrap:wrap;}
.chamber-toggle{display:flex;background:var(--surface2);border:1px solid var(--border);border-radius:8px;overflow:hidden;}
.chamber-btn{background:transparent;border:none;padding:0.45rem 1.2rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);cursor:pointer;letter-spacing:0.08em;transition:all 0.18s;}
.chamber-btn.active{background:var(--gold-glow);color:var(--gold);}
.map-legend{display:flex;align-items:center;gap:0.75rem;margin-left:auto;}
.legend-item{display:flex;align-items:center;gap:0.35rem;font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);}
.legend-dot{width:10px;height:10px;border-radius:3px;}
.legend-dot.D{background:var(--dem);}
.legend-dot.R{background:var(--rep);}
.map-count{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);}
.map-svg-wrap{flex:1;overflow:hidden;position:relative;display:flex;align-items:center;justify-content:center;background:var(--bg);}
.map-loading{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;z-index:5;}
.map-loading-ring{width:40px;height:40px;border:2px solid var(--border);border-top-color:var(--gold);border-radius:50%;animation:spin 1s linear infinite;}
@keyframes spin{to{transform:rotate(360deg)}}
.map-loading-text{font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);letter-spacing:0.1em;}
.map-error{font-family:var(--font-mono);font-size:0.72rem;color:#d43b3b;text-align:center;max-width:340px;line-height:1.7;padding:2rem;}
.map-tooltip{position:absolute;background:var(--charcoal);border:1px solid var(--gold-dim);border-radius:8px;padding:0.6rem 0.85rem;pointer-events:none;z-index:30;min-width:160px;box-shadow:0 8px 32px rgba(0,0,0,0.6);}
.map-tooltip-district{font-family:var(--font-mono);font-size:0.58rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:0.2rem;}
.map-tooltip-name{font-size:0.82rem;font-weight:500;color:var(--text);}
.map-tooltip-party{font-size:0.68rem;font-family:var(--font-mono);}
.map-tooltip-party.D{color:var(--dem);}
.map-tooltip-party.R{color:var(--rep);}
.zoom-controls{position:absolute;bottom:1.5rem;right:1.5rem;display:flex;flex-direction:column;gap:0.4rem;z-index:10;}
.zoom-btn{width:34px;height:34px;background:var(--surface);border:1px solid var(--border);border-radius:6px;color:var(--text);font-size:1.1rem;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all 0.18s;}
.zoom-btn:hover{border-color:var(--gold-dim);color:var(--gold);}
.map-hint{position:absolute;bottom:1.5rem;left:50%;transform:translateX(-50%);font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);letter-spacing:0.08em;background:var(--surface);border:1px solid var(--border);border-radius:100px;padding:0.3rem 0.85rem;pointer-events:none;opacity:0.7;white-space:nowrap;}
.map-right{width:340px;flex-shrink:0;background:var(--surface);border-left:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;}
.map-right-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.75rem;padding:2rem;}
.map-right-empty-icon{font-size:2.5rem;opacity:0.2;}
.map-right-empty-text{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);text-align:center;line-height:1.7;letter-spacing:0.04em;}
.district-panel{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.district-header{padding:1.25rem;border-bottom:1px solid var(--border);position:sticky;top:0;background:var(--surface);z-index:5;}
.district-num{font-family:var(--font-mono);font-size:0.58rem;color:var(--gold);letter-spacing:0.12em;text-transform:uppercase;margin-bottom:0.2rem;}
.district-region{font-family:var(--font-display);font-size:1.05rem;font-weight:700;color:var(--text);line-height:1.2;}
.section-label{font-family:var(--font-mono);font-size:0.58rem;color:var(--gold);letter-spacing:0.12em;text-transform:uppercase;padding:0.9rem 1.25rem 0.4rem;border-top:1px solid var(--border);}
.leg-card{margin:0.5rem 1rem 0;background:var(--surface2);border:1px solid var(--border);border-radius:var(--r);padding:1rem;}
.leg-card-header{display:flex;align-items:center;gap:0.75rem;margin-bottom:0.75rem;}
.leg-card-avatar{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-family:var(--font-display);font-weight:800;font-size:0.9rem;flex-shrink:0;}
.leg-card-avatar.D{background:var(--dem-bg);color:var(--dem);}
.leg-card-avatar.R{background:var(--rep-bg);color:var(--rep);}
.leg-card-name{font-size:0.9rem;font-weight:500;color:var(--text);line-height:1.2;}
.leg-card-sub{font-size:0.66rem;color:var(--muted);font-family:var(--font-mono);}
.leg-card-bio{font-size:0.78rem;color:var(--muted);line-height:1.6;font-weight:300;}
.leg-card-issues{display:flex;flex-wrap:wrap;gap:0.3rem;margin-top:0.6rem;}
.issue-tag{background:var(--charcoal);border:1px solid var(--border);border-radius:4px;padding:0.15rem 0.45rem;font-size:0.65rem;color:var(--muted);}
.stat-grid{display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;padding:0.5rem 1rem 0.75rem;}
.stat-card{background:var(--surface2);border:1px solid var(--border);border-radius:var(--r);padding:0.75rem;}
.stat-label{font-family:var(--font-mono);font-size:0.56rem;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;margin-bottom:0.25rem;}
.stat-value{font-family:var(--font-display);font-size:1.1rem;font-weight:700;color:var(--text);}
.stat-sub{font-size:0.62rem;color:var(--muted);}
.stat-loading{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);text-align:center;padding:1.5rem;letter-spacing:0.06em;}
.stat-error{font-family:var(--font-mono);font-size:0.65rem;color:#c97a3b;padding:0.75rem 1.25rem;line-height:1.6;background:rgba(212,100,59,0.06);border:1px solid rgba(212,100,59,0.15);border-radius:6px;margin:0.5rem 1rem;}
.census-source{font-family:var(--font-mono);font-size:0.54rem;color:var(--muted);padding:0.4rem 1rem 1.25rem;opacity:0.55;}
.industry-list{padding:0.25rem 1rem 0.75rem;}
.industry-bar-wrap{margin-bottom:0.65rem;}
.industry-row{display:flex;justify-content:space-between;font-size:0.73rem;color:var(--text);margin-bottom:0.28rem;}
.industry-pct{font-family:var(--font-mono);font-size:0.64rem;color:var(--muted);}
.industry-bar-track{height:4px;background:var(--charcoal);border-radius:100px;overflow:hidden;}
.industry-bar-fill{height:100%;background:var(--gold);border-radius:100px;transition:width 0.4s ease;}
.committees-list{padding:0 1rem 1rem;}
.committee-tag{display:block;font-size:0.72rem;color:var(--text);padding:0.3rem 0;border-bottom:1px solid var(--border);font-family:var(--font-mono);}
.committee-tag:last-child{border-bottom:none;}
@media(max-width:1100px){.map-right{width:300px;}}
@media(max-width:900px){.map-right{display:none;}}
@media(max-width:768px){
  .masthead-tagline,.masthead-badge{display:none;}
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
  .map-left{width:260px;}
}
@media(max-width:600px){.map-left{display:none;}}
@media(max-width:1200px){
  .mast-btn{padding:0.3rem 0.45rem;font-size:0.58rem;letter-spacing:0.03em;}
  .masthead-nav{gap:0.2rem;}
}
@media(max-width:900px){
  .mast-btn{padding:0.28rem 0.38rem;font-size:0.55rem;}
  .masthead-nav{gap:0.15rem;}
}
@media(max-width:768px){
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
}

/* ── LIGHT MODE OVERRIDES ── */
[data-theme="light"] body{background:#eef1f7;color:#1a1e2e;}
[data-theme="light"] .page,[data-theme="light"] .app,[data-theme="light"] .bt-app,[data-theme="light"] .tl-app,[data-theme="light"] .map-app,[data-theme="light"] .cf-app{background:#eef1f7;}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .masthead-wordmark sup{color:rgba(230,228,220,0.5);}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .mast-btn-sm:hover{color:#ffffff;border-color:#c9a84c;background:rgba(201,168,76,0.1);}
[data-theme="light"] .mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.7);color:#ff6b6b;background:rgba(192,57,43,0.1);}
[data-theme="light"] .sidebar,[data-theme="light"] .bt-left,[data-theme="light"] .cal-sidebar,[data-theme="light"] .cf-sidebar,[data-theme="light"] .map-left{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .main,[data-theme="light"] .bt-detail,[data-theme="light"] .cf-main{background:#eef1f7;}
[data-theme="light"] .filter-btn,[data-theme="light"] .vh-filter-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .filter-btn.active,[data-theme="light"] .vh-filter-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .filter-btn.dem-active{background:var(--dem-bg);border-color:var(--dem);color:var(--dem);}
[data-theme="light"] .filter-btn.rep-active{background:var(--rep-bg);border-color:var(--rep);color:var(--rep);}
[data-theme="light"] .search-input,[data-theme="light"] .chat-input,[data-theme="light"] .bt-search-input,[data-theme="light"] .tl-search-input{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .leg-item.selected{background:#e8ecf4;}
[data-theme="light"] .leg-item.selected::before{background:#1a2744;}
[data-theme="light"] .leg-item:hover{background:#e8ecf4;}
[data-theme="light"] .chat-input-area{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .msg-bubble{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .msg.user .msg-bubble{background:#e8ecf4;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .send-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .main-topbar,[data-theme="light"] .news-topbar,[data-theme="light"] .chamber-bar,[data-theme="light"] .toggle-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-header,[data-theme="light"] .vh-filters,[data-theme="light"] .vh-stats{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-bill-number,[data-theme="light"] .bt-bill-number,[data-theme="light"] .bt-detail-number,[data-theme="light"] .tl-bill-number,[data-theme="light"] .district-num{color:#1a2744;}
[data-theme="light"] .profile-section-label,[data-theme="light"] .section-label,[data-theme="light"] .form-label,[data-theme="light"] .panel-title,[data-theme="light"] .output-label,[data-theme="light"] .floor-title,[data-theme="light"] .detail-title,[data-theme="light"] .search-section-label{color:#1a2744;}
[data-theme="light"] .input-panel{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .output-panel,[data-theme="light"] .output-body{background:#eef1f7;}
[data-theme="light"] .tl-add-btn,[data-theme="light"] .tl-refresh-btn,[data-theme="light"] .tl-view-btn{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .tl-table-head{background:#e8ecf4;}
[data-theme="light"] .tl-tr:hover{background:#e8ecf4;}
[data-theme="light"] .date-group-header{color:#1a2744;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .detail-header,[data-theme="light"] .profile-header,[data-theme="light"] .donors-section,[data-theme="light"] .bt-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .acct-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .story-source{color:#6b7280;}
[data-theme="light"] .profile-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .home-title{color:#1a2744;}
[data-theme="light"] .cf-amount,[data-theme="light"] .cf-total{color:#1a2744;}
[data-theme="light"] .chamber-btn.active{background:rgba(26,39,68,0.1);color:#1a2744;border-color:rgba(26,39,68,0.3);}
[data-theme="light"] .swing-chip{background:rgba(26,39,68,0.1);color:#1a2744;}
[data-theme="light"] .score-cell.pass{background:rgba(37,107,66,0.12);}
[data-theme="light"] .score-cell.fail{background:rgba(176,48,32,0.08);}
[data-theme="light"] .map-tooltip-district{color:#1a2744;}
[data-theme="light"] .watchlist-btn-small,[data-theme="light"] .watch-btn{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .vote-history-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.35);color:#a07830;}
[data-theme="light"] .masthead-badge{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .masthead-badge-dot{background:#1a2744;}
[data-theme="light"] .mast-btn.gold{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .mast-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .issue-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .qp-btn{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .empty-card{background:#f0f2f8;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .donor-bar-fill{background:linear-gradient(90deg,#1a2744,#2d4a80);}
[data-theme="light"] .cf-leg-item.active,[data-theme="light"] .bt-bill-item.selected{border-left-color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .plan-badge,[data-theme="light"] .admin{color:#1a2744;}
[data-theme="light"] .login-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 4px 24px rgba(26,39,68,0.1);}
`;

/* ─── SCHEMATIC FALLBACK MAP ────────────────────────────── */
// Renders a color-coded grid of district cells when GeoJSON is unavailable.
// Fully interactive — clicking a cell selects the district just like the real map.
function SchematicMap({ chamber, selectedDistrict, onDistrictClick, highlightDistricts }) {
  const legislators = chamber === "Senate" ? SENATORS : REPRESENTATIVES;
  const cols = chamber === "Senate" ? 8 : 14;

  const getFill = (leg) => {
    const sel = selectedDistrict === leg.district;
    const hi  = highlightDistricts?.includes(leg.district);
    if (leg.party === "D") {
      if (sel) return "rgba(59,111,212,0.78)";
      if (hi)  return "rgba(59,111,212,0.52)";
      return "rgba(59,111,212,0.22)";
    }
    if (leg.party === "R") {
      if (sel) return "rgba(212,59,59,0.78)";
      if (hi)  return "rgba(212,59,59,0.52)";
      return "rgba(212,59,59,0.22)";
    }
    return "#1e1e28";
  };

  return (
    <div style={{ width:"100%", height:"100%", overflow:"auto", padding:"1.5rem" }}>
      <div style={{ fontFamily:"var(--font-mono)", fontSize:"0.6rem", color:"var(--muted)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:"1rem", textAlign:"center" }}>
        Michigan {chamber} Districts — Schematic View
      </div>
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${cols}, 1fr)`, gap:"4px", maxWidth:"900px", margin:"0 auto" }}>
        {legislators.map(leg => (
          <div
            key={leg.id}
            onClick={() => onDistrictClick(leg.district)}
            title={`${leg.name} · D${leg.district} · ${leg.region}`}
            style={{
              background: getFill(leg),
              border: selectedDistrict === leg.district ? "1.5px solid var(--gold)" : "1px solid rgba(255,255,255,0.07)",
              borderRadius: "4px",
              padding: "6px 2px",
              cursor: "pointer",
              textAlign: "center",
              transition: "background 0.15s",
            }}
          >
            <div style={{ fontFamily:"var(--font-mono)", fontSize:"0.65rem", color:"rgba(255,255,255,0.8)", fontWeight:600 }}>{leg.district}</div>
            {chamber === "Senate" && (
              <div style={{ fontSize:"0.5rem", color:"rgba(255,255,255,0.4)", marginTop:"2px", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", padding:"0 2px" }}>
                {leg.name.split(" ").pop()}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontFamily:"var(--font-mono)", fontSize:"0.58rem", color:"var(--muted)", textAlign:"center", marginTop:"1rem" }}>
        Geographic map unavailable — click any district to view details
      </div>
    </div>
  );
}

/* ─── MAP COMPONENT ─────────────────────────────────────── */
function MichiganMap({ chamber, selectedDistrict, onDistrictClick, highlightDistricts }) {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const [geoData, setGeoData] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [d3Ready, setD3Ready] = useState(!!window.d3);

  useEffect(() => {
    if (window.d3) { setD3Ready(true); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js";
    s.onload = () => setD3Ready(true);
    s.onerror = () => setMapError("Failed to load map library (D3).");
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    if (!d3Ready) return;
    setMapLoading(true);
    setGeoData(null);
    setMapError(null);

    // Load from local public folder — these files are already in your repo
    // legisim-frontend/public/mi_senate.geojson and mi_house.geojson
    const localUrl = chamber === "Senate" ? "/mi_senate.geojson" : "/mi_house.geojson";

    const tryFetch = (url) =>
      fetch(url)
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(data => {
          if (!data || !Array.isArray(data.features) || data.features.length === 0) {
            throw new Error("Response contained no features");
          }
          return data;
        });

    tryFetch(localUrl)
      .then(data => { setGeoData(data); setMapLoading(false); })
      .catch(() => {
        // File not found — show schematic fallback, do NOT crash
        setGeoData("fallback");
        setMapLoading(false);
      });
  }, [d3Ready, chamber]);

  useEffect(() => {
    if (!d3Ready || !geoData || geoData === "fallback" || !svgRef.current) return;
    const d3 = window.d3;
    const svg = d3.select(svgRef.current);
    svg.selectAll(".district-layer").remove();

    const w = svgRef.current.clientWidth || 600;
    const h = svgRef.current.clientHeight || 500;
    const projection = d3.geoMercator().fitSize([w - 40, h - 40], geoData);
    const path = d3.geoPath().projection(projection);
    const legislators = chamber === "Senate" ? SENATORS : REPRESENTATIVES;

    const getDistNum = d => {
      // Census GENZ files use SLDUST (Senate) or SLDLST (House) — zero-padded strings e.g. "016"
      const v = d.properties.SLDUST || d.properties.SLDLST ||
                d.properties.DISTRICT || d.properties.district ||
                d.properties.NAME || "0";
      return parseInt(String(v), 10); // parseInt handles leading zeros automatically
    };

    const getFill = (distNum, hover = false) => {
      const leg = legislators.find(l => l.district === distNum);
      if (!leg || leg.party === "-") return "var(--surface2)";
      const sel = selectedDistrict === distNum;
      const hi = highlightDistricts?.includes(distNum);
      if (leg.party === "D") {
        if (sel) return "#2952b3";
        if (hover || hi) return "#2255a8";
        return "#3d6cc4";
      }
      if (sel) return "#c43030";
      if (hover || hi) return "#a01818";
      return "#d44444";
    };

    const layer = svg.append("g").attr("class", "district-layer")
      .attr("transform", `translate(${transform.x},${transform.y}) scale(${transform.k})`);

    layer.selectAll("path").data(geoData.features).enter().append("path")
      .attr("d", path)
      .attr("fill", d => getFill(getDistNum(d)))
      .attr("stroke", d => getDistNum(d) === selectedDistrict ? "var(--gold)" : "var(--map-stroke, rgba(255,255,255,0.15))")
      .attr("stroke-width", d => (getDistNum(d) === selectedDistrict ? 1.5 : 0.5) / transform.k)
      .style("cursor", "pointer")
      .on("mouseover", function(event, d) {
        const n = getDistNum(d);
        const leg = legislators.find(l => l.district === n);
        if (!leg) return;
        d3.select(this).attr("fill", getFill(n, true));
        const rect = wrapRef.current.getBoundingClientRect();
        setTooltip({ x: event.clientX - rect.left, y: event.clientY - rect.top, leg, distNum: n });
      })
      .on("mousemove", function(event) {
        const rect = wrapRef.current.getBoundingClientRect();
        setTooltip(t => t ? { ...t, x: event.clientX - rect.left, y: event.clientY - rect.top } : t);
      })
      .on("mouseout", function(event, d) {
        d3.select(this).attr("fill", getFill(getDistNum(d)));
        setTooltip(null);
      })
      .on("click", function(event, d) { onDistrictClick(getDistNum(d)); });

    if (transform.k > 1.8) {
      layer.selectAll(".dlabel").data(geoData.features).enter().append("text")
        .attr("class", "dlabel")
        .attr("transform", d => { const c = path.centroid(d); return isNaN(c[0]) ? "" : `translate(${c[0]},${c[1]})`; })
        .attr("text-anchor", "middle").attr("dominant-baseline", "middle")
        .attr("font-size", 7 / transform.k).attr("fill", "rgba(255,255,255,0.65)")
        .attr("font-family", "IBM Plex Mono, monospace").attr("pointer-events", "none")
        .text(d => getDistNum(d) || "");
    }
  }, [d3Ready, geoData, chamber, selectedDistrict, highlightDistricts, transform]);

  const handleWheel = useCallback(e => {
    e.preventDefault();
    setTransform(t => ({ ...t, k: Math.max(0.5, Math.min(10, t.k * (e.deltaY < 0 ? 1.15 : 0.87))) }));
  }, []);
  const handleMouseDown = useCallback(e => {
    if (e.button !== 0) return;
    setDragging(true);
    dragStart.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
  }, [transform]);
  const handleMouseMove = useCallback(e => {
    if (!dragging || !dragStart.current) return;
    setTransform(t => ({ ...t, x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y }));
  }, [dragging]);
  const handleMouseUp = useCallback(() => setDragging(false), []);
  const zoom = dir => setTransform(t => ({ ...t, k: Math.max(0.5, Math.min(10, t.k * (dir > 0 ? 1.3 : 0.77))) }));
  const reset = () => setTransform({ x: 0, y: 0, k: 1 });

  return (
    <div ref={wrapRef} style={{ width:"100%", height:"100%", position:"relative" }}
      onWheel={handleWheel} onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
    >
      {(mapLoading || !d3Ready) && (
        <div className="map-loading">
          <div className="map-loading-ring" />
          <div className="map-loading-text">Loading Michigan {chamber} districts…</div>
        </div>
      )}
      {mapError && <div className="map-loading"><div className="map-error">{mapError}</div></div>}

      {/* Schematic fallback when GeoJSON is unavailable */}
      {geoData === "fallback" && (
        <SchematicMap
          chamber={chamber}
          selectedDistrict={selectedDistrict}
          onDistrictClick={onDistrictClick}
          highlightDistricts={highlightDistricts}
        />
      )}

      <svg ref={svgRef} style={{ width:"100%", height:"100%", cursor: dragging ? "grabbing" : "grab", display: geoData && geoData !== "fallback" ? "block" : "none" }} />
      {tooltip && (
        <div className="map-tooltip" style={{
          left: Math.min(tooltip.x + 14, (wrapRef.current?.clientWidth || 600) - 190),
          top: Math.max(8, tooltip.y - 60),
        }}>
          <div className="map-tooltip-district">{chamber} District {tooltip.distNum}</div>
          <div className="map-tooltip-name">{tooltip.leg.name}</div>
          <div className={`map-tooltip-party ${tooltip.leg.party}`}>
            {tooltip.leg.party === "D" ? "Democrat" : tooltip.leg.party === "R" ? "Republican" : "Vacant"}
          </div>
        </div>
      )}
      <div className="zoom-controls">
        <button className="zoom-btn" onClick={() => zoom(1)}>+</button>
        <button className="zoom-btn" onClick={() => zoom(-1)}>−</button>
        <button className="zoom-btn" onClick={reset} style={{ fontSize:"0.7rem" }}>⌂</button>
      </div>
      {geoData && geoData !== "fallback" && <div className="map-hint">Scroll to zoom · Drag to pan · Click a district</div>}
    </div>
  );
}

/* ─── DISTRICT PANEL ────────────────────────────────────── */
function DistrictPanel({ chamber, districtNum, getPhoto = () => null }) {
  const [censusData, setCensusData] = useState(null);
  const [censusLoading, setCensusLoading] = useState(false);
  const [censusError, setCensusError] = useState(null);

  const legislators = chamber === "Senate" ? SENATORS : REPRESENTATIVES;
  const leg = districtNum ? legislators.find(l => l.district === districtNum) : null;

  useEffect(() => {
    if (!districtNum) return;
    setCensusData(null);
    setCensusError(null);
    setCensusLoading(true);

    fetchCensusDistricts(chamber)
      .then(data => {
        const d = data[districtNum];
        if (!d) throw new Error(`No Census data returned for ${chamber} District ${districtNum}.`);
        setCensusData(d);
        setCensusLoading(false);
      })
      .catch(err => {
        setCensusError(err.message);
        setCensusLoading(false);
      });
  }, [chamber, districtNum]);

  if (!districtNum) {
    return (
      <div className="map-right-empty">
        <div className="map-right-empty-icon">🗺</div>
        <div className="map-right-empty-text">Click any district on the map to view legislator info and live Census demographic data.</div>
      </div>
    );
  }

  if (!leg) return (
    <div className="map-right-empty">
      <div className="map-right-empty-text">No data for District {districtNum}.</div>
    </div>
  );

  const initials = leg.name.split(" ").filter(Boolean).map(n => n[0]).join("").slice(0, 2);
  const topInd = censusData?.industries?.[0]?.pct || 1;

  return (
    <div className="district-panel">
      <div className="district-header">
        <div className="district-num">{chamber} District {districtNum}</div>
        <div className="district-region">{leg.region}</div>
      </div>
      <div className="section-label">Legislator</div>
      <div className="leg-card">
        <div className="leg-card-header">
          {(() => { const photo = getPhoto(leg.name); return photo
            ? <img src={photo} alt={leg.name} style={{width:"52px",height:"52px",borderRadius:"50%",objectFit:"cover",flexShrink:0}} />
            : <div className={`leg-card-avatar ${leg.party}`}>{initials}</div>; })()}
          <div>
            <div className="leg-card-name">{leg.name}</div>
            <div className="leg-card-sub">
              {leg.party === "D" ? "Democrat" : leg.party === "R" ? "Republican" : "Vacant"} · {chamber} D{districtNum}
            </div>
          </div>
        </div>
        <div className="leg-card-bio">{leg.bio}</div>
        {leg.keyIssues?.length > 0 && (
          <div className="leg-card-issues">
            {leg.keyIssues.map(i => <span key={i} className="issue-tag">{i}</span>)}
          </div>
        )}
      </div>

      <div className="section-label">Demographics · ACS 2022 5-Year</div>
      {censusLoading && <div className="stat-loading">Fetching Census data…</div>}
      {censusError && (
        <div className="stat-error">
          {censusError}
          {!CENSUS_KEY && <><br /><br />Add <code>REACT_APP_CENSUS_API_KEY=your_key</code> to your <code>.env</code> file to enable live data.</>}
        </div>
      )}
      {censusData && (
        <>
          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-label">Population</div>
              <div className="stat-value">{censusData.population.toLocaleString()}</div>
              <div className="stat-sub">Total residents</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Median Income</div>
              <div className="stat-value">{censusData.medianIncome ? "$" + censusData.medianIncome.toLocaleString() : "N/A"}</div>
              <div className="stat-sub">Per household/yr</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">College Grads</div>
              <div className="stat-value">{censusData.pctCollege}%</div>
              <div className="stat-sub">Adults 25+ w/ degree</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Unemployment</div>
              <div className="stat-value">{censusData.pctUnemployment}%</div>
              <div className="stat-sub">Civilian labor force</div>
            </div>
          </div>

          {censusData.industries?.length > 0 && (
            <>
              <div className="section-label">Employment by Industry</div>
              <div className="industry-list">
                {censusData.industries.map(ind => (
                  <div key={ind.label} className="industry-bar-wrap">
                    <div className="industry-row">
                      <span>{ind.label}</span>
                      <span className="industry-pct">{ind.pct}%</span>
                    </div>
                    <div className="industry-bar-track">
                      <div className="industry-bar-fill" style={{ width: `${(ind.pct / topInd) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="census-source">
            Source: U.S. Census Bureau, American Community Survey 5-Year Estimates ({CENSUS_YEAR})
          </div>
        </>
      )}


    </div>
  );
}

/* ─── MAIN PAGE ─────────────────────────────────────────── */
async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate('home');
}

export default function DistrictMapPage({ onNavigate }) {
  const [chamber, setChamber] = useState("Senate");
  const [dirMap, setDirMap] = useState({});

  useEffect(() => {
    fetch("https://legisim-server.onrender.com/api/directory?category=Legislature")
      .then(r => r.json())
      .then(d => {
        const map = {};
        (d.entries || []).forEach(e => { if (e.photo_url) map[e.name.toLowerCase()] = e.photo_url; });
        setDirMap(map);
      })
      .catch(() => {});
  }, []);

  const getPhoto = (name) => {
    if (!name) return null;
    const lower = name.toLowerCase();
    if (dirMap[lower]) return dirMap[lower];
    const last = lower.split(" ").pop();
    return Object.entries(dirMap).find(([k]) => k.includes(last))?.[1] || null;
  };
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [search, setSearch] = useState("");

  const legislators = chamber === "Senate" ? SENATORS : REPRESENTATIVES;
  const query = search.trim().toLowerCase();
  const matchedLegs = query
    ? legislators.filter(l =>
        l.name.toLowerCase().includes(query) ||
        String(l.district).includes(query) ||
        l.region.toLowerCase().includes(query) ||
        (l.committees || []).some(c => c.toLowerCase().includes(query))
      )
    : [];

  const handleDistrictClick = n => { if (n) { setSelectedDistrict(n); setSearch(""); } };
  const handleSearchSelect = leg => { setSelectedDistrict(leg.district); setSearch(""); };
  const highlightDistricts = query ? matchedLegs.map(l => l.district) : null;
  const displayList = query ? matchedLegs : legislators;
  const dCount = legislators.filter(l => l.party === "D").length;
  const rCount = legislators.filter(l => l.party === "R").length;

  return (
    <div className="map-app">
      <style>{CSS}</style>
      <MobileMenuCSS />
      <div className="legisim-masthead">
        <div className="masthead-left">
          <div className="masthead-wordmark">Whip<em>Count</em><sup>TM</sup></div>
          <button className="mast-back" onClick={() => onNavigate("dashboard")}>← Dashboard</button>
        </div>
        <div className="masthead-right">
          <button className="mast-btn-sm" onClick={() => onNavigate("account")}>Account</button>
          <button className="mast-btn-sm signout" onClick={() => handleSignOut(supabase, onNavigate)}>Sign Out</button>
        </div>
        </div>
        <div className="map-body">
        <div className="map-left">
          <div className="map-left-header">
            <div className="map-left-title">Search Districts</div>
            <div className="search-wrap">
              <svg className="search-icon" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="22" y2="22" />
              </svg>
              <input className="search-input" placeholder="District, legislator, county…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="search-results">
            <div className="search-section-label">
              {query ? `${matchedLegs.length} result${matchedLegs.length !== 1 ? "s" : ""}` : `All ${chamber} Districts`}
            </div>
            {displayList.length === 0 && query && (
              <div style={{ padding:"2rem 1.25rem", fontFamily:"var(--font-mono)", fontSize:"0.68rem", color:"var(--muted)", textAlign:"center" }}>
                No results for "{search}"
              </div>
            )}
            {displayList.map(leg => {
              const initials = leg.name.split(" ").filter(Boolean).map(n => n[0]).join("").slice(0, 2);
              return (
                <div key={leg.id}
                  className={`search-result-item ${selectedDistrict === leg.district ? "selected" : ""}`}
                  onClick={() => handleSearchSelect(leg)}
                >
                  {(() => { const photo = getPhoto(leg.name); return photo
                    ? <img src={photo} alt={leg.name} style={{width:"36px",height:"36px",borderRadius:"50%",objectFit:"cover",flexShrink:0}} />
                    : <div className={`sri-avatar ${leg.party}`}>{initials}</div>; })()}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div className="sri-name">{leg.name}</div>
                    <div className="sri-sub">D{leg.district} · {leg.region.split("/")[0]}</div>
                  </div>
                  <span className={`sri-badge ${leg.party}`}>{leg.party === "D" ? "DEM" : leg.party === "R" ? "REP" : "—"}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="map-center">
          <div className="map-toolbar">
            <div className="chamber-toggle">
              <button className={`chamber-btn ${chamber === "Senate" ? "active" : ""}`}
                onClick={() => { setChamber("Senate"); setSelectedDistrict(null); }}>Senate (38)</button>
              <button className={`chamber-btn ${chamber === "House" ? "active" : ""}`}
                onClick={() => { setChamber("House"); setSelectedDistrict(null); }}>House (110)</button>
            </div>
            <div className="map-count">{dCount}D / {rCount}R</div>
            <div className="map-legend">
              <div className="legend-item"><div className="legend-dot D" />Democrat</div>
              <div className="legend-item"><div className="legend-dot R" />Republican</div>
            </div>
          </div>
          <div className="map-svg-wrap">
            <MichiganMap chamber={chamber} selectedDistrict={selectedDistrict}
              onDistrictClick={handleDistrictClick} highlightDistricts={highlightDistricts} />
          </div>
        </div>

        <div className="map-right">
          <DistrictPanel chamber={chamber} districtNum={selectedDistrict} getPhoto={getPhoto} />
        </div>
      </div>
    </div>
  );
}
